<template>
  <div
    class="search-input-bar"
    :class="{ '--open': searchActive }"
    @keydown.down.prevent="
      () => {
        triggerAutocompleteSuggestionsKeyboardNav();
      }
    "
    @keydown.up.prevent="
      () => {
        triggerAutocompleteSuggestionsKeyboardNav();
      }
    "
  >
    <q-form
      class="search-input-bar__form"
      @submit="
        () => {
          handleSubmit('input', { input: userInput });
          userInput = '';
        }
      "
    >
      <q-input
        ref="SearchInput"
        v-model="userInput"
        standout
        :focused="searchActive"
        :class="{ 'q-field--highlighted q-field--focused': searchActive }"
        dense
        placeholder="Search by make, model, or keyword"
        class="search-input-bar__input --shadow__bottom"
        :disable="disable"
        debounce="300"
        @keydown="
          e => {
            if (e.keyCode == '38' || e.keyCode == '40') e.preventDefault();
          }
        "
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        maxlength="500"
      >
        <template v-slot:prepend>
          <q-icon name="sym_r_search" size="22px" style="margin-bottom: -2px" />
          <q-separator vertical inset class="q-mx-sm" />
        </template>
        <template v-slot:append>
          <q-btn
            round
            dense
            :flat="!voiceRecognitionListening"
            :color="voiceRecognitionListening ? 'secondary' : 'text-text-light'"
            icon="sym_r_mic"
            :class="{ '--pulse': voiceRecognitionListening }"
            @click="
              () => {
                if (voiceRecognitionListening) {
                  voiceRecognition.stop();
                  voiceRecognitionListening = false;
                } else {
                  voiceRecognition.start();
                  voiceRecognitionListening = true;
                }
              }
            "
            size="16px"
          >
            <q-tooltip
              anchor="center left"
              self="center right"
              :offset="[6, 6]"
            >
              {{
                voiceRecognitionListening ? "Listening..." : "Search By Voice"
              }}
            </q-tooltip>
          </q-btn>
          <q-btn
            v-if="searchActive"
            round
            dense
            color="secondary"
            icon="sym_r_send"
            size="17px"
            type="submit"
            class="--shadow__bottom q-ml-xs"
          >
            <q-tooltip
                anchor="center left"
                self="center right"
                :offset="[6, 6]"
            >
              Submit Search
            </q-tooltip>
          </q-btn>
          <q-btn
              v-if="searchActive && windowWidth <= 1025"
              round
              dense
              flat
              icon="sym_r_close"
              size="17px"
              @click="closeSearch"
            class="q-ml-xs"
          />
          <!--<q-btn
            round
            dense
            push
            color="secondary"
            icon="sym_r_send"
            @click="
              () => {
                $emit('submitted', { input: userInput });
                userInput = '';
              }
            "
            :disable="disable"
          />-->
        </template>
      </q-input>
    </q-form>

    <transition appear appear-class="fade-fast" name="fade" mode="out-in">
      <div v-if="searchActive" class="search-widget__backdrop"></div>
    </transition>

    <transition appear appear-class="fade-fast" name="fade" mode="out-in">
      <div
        v-if="searchActive"
        class="search-widget__dropdown-container"
        @mousedown.prevent
      >
        <div>
          <div
            v-show="userInput.length > 0"
            ref="suggestedSearchesContainer"
            class="suggested-searches-container q-px-md q-my-sm q-pb-xs outline-none"
            :class="{
              '--scrolled-to-bottom': isSuggestedSearchesScrolledToBottom
            }"
            style="max-height: 266px; overflow-y: auto"
            @keydown.up.prevent="moveDropdownAutocompleteSuggestionFocus(-1)"
            @keydown.down.prevent="moveDropdownAutocompleteSuggestionFocus(1)"
            @keydown.enter.prevent="
              () => {
                handleSubmit('input', {
                  input:
                    autocompleteSuggestions.length > 0
                      ? autocompleteSuggestions[
                          activeDropdownAutocompleteSuggestionIndex
                        ].keyword
                      : userInput
                });
              }
            "
            @mouseover="
              () => {
                usingAutocompleteSuggestionsKeyboardNavigation = false;
              }
            "
            @scroll="handleSuggestedSearchesScroll"
            tabindex="0"
          >
            <div class="text-text-light q-mb-md --text-sm text-weight-medium">
              Search Suggestions...
            </div>

            <div
              v-if="autocompleteSuggestions.length == 0 && userInput.length > 0"
              :key="0 + '_autocompleteSuggestion'"
              class="q-mb-sm q-pb-xs"
              :ref="0 + '_dropdownAutocompleteSuggestionItem'"
            >
              <q-chip
                color="background-light"
                text-color="white"
                @click="
                  () => {
                    handleSubmit('input', {
                      input: userInput
                    });
                  }
                "
                clickable
                class="suggestion-chip--min-hover --shadow__bottom --bordered --hover-lighten full-width"
                style="display: block; height: auto; padding-top: 10px !important; padding-bottom: 10px !important;"
                :class="{
                  '--autocomplete-suggestion-hover':
                    usingAutocompleteSuggestionsKeyboardNavigation &&
                    activeDropdownAutocompleteSuggestionIndex == 0
                }"
              >
                <q-icon
                  name="sym_r_auto_awesome"
                  color="secondary"
                  size="21px"
                  class="q-mr-sm"
                />
                <div class="column col">
                  <div
                    v-html="highlightedAutocompleteSuggestion(userInput)"
                    class="text-text-light full-width ellipsis"
                  />
                </div>
              </q-chip>
            </div>

            <div
              v-for="(suggestion, i) in autocompleteSuggestions"
              :key="i + '_autocompleteSuggestion'"
              class="q-mb-sm"
              :ref="i + '_dropdownAutocompleteSuggestionItem'"
            >
              <q-chip
                color="background-light"
                text-color="white"
                @click="
                  () => {
                    handleSubmit('filterClauses', suggestion.search_filters);
                  }
                "
                clickable
                class="suggestion-chip--min-hover --shadow__bottom --bordered --hover-lighten full-width"
                style="display: block; height: auto; padding-top: 10px !important; padding-bottom: 10px !important;"
                :class="{
                  '--autocomplete-suggestion-hover':
                    usingAutocompleteSuggestionsKeyboardNavigation &&
                    activeDropdownAutocompleteSuggestionIndex == i
                }"
              >
                <q-icon
                  :name="
                    suggestion.aiSuggestion
                      ? 'sym_r_auto_awesome'
                      : 'sym_r_search'
                  "
                  :color="suggestion.aiSuggestion ? 'secondary' : 'white'"
                  size="21px"
                  class="q-mr-sm"
                />
                <div class="column col">
                  <div
                    v-html="
                      highlightedAutocompleteSuggestion(suggestion.keyword)
                    "
                    class="text-text-light full-width ellipsis"
                  />
                </div>
              </q-chip>
            </div>
          </div>

          <transition
            appear
            appear-class="fade-fast"
            name="fade-fast"
            mode="out-in"
          >
            <div
              v-if="
                userInput.length == 0 && previousSuggestedResponses.length > 0
              "
              class="q-px-md q-my-sm q-pb-xs"
            >
              <div class="flex items-center justify-start">
                <!--<q-icon name="sym_r_youtube_searched_for" class="q-mr-sm" size="20px" />-->
                <div
                  class="text-weight-medium q-pb-xs q-mb-xs"
                  style="font-size: 14px"
                >
                  Recent Searches
                </div>
              </div>
              <HorizontalScrollable
                v-if="previousSuggestedResponses.length > 0"
                class="search-input-bar__suggestions-container q-mt-sm relative-position"
              >
                <div
                  class="search-input-bar__suggestions flex items-center q-gutter-xs no-wrap"
                >
                  <!--<div class="flex items-center no-wrap q-mr-xs">
                    <q-icon
                        name="sym_r_youtube_searched_for"
                        size="26px"
                        style="margin-bottom: -2px"
                    />
                  </div>-->
                  <div
                    v-for="(previousSuggestedResponse,
                    i) in previousSuggestedResponses"
                    :key="i + '_previousSearchSuggestedResponse'"
                  >
                    <q-chip
                      color="background-light"
                      text-color="white"
                      @mousedown="horizontalScrollClickStart"
                      @mouseup="
                        e => {
                          horizontalScrollClickEnd(e, () => {
                            handleSubmit('input', {
                              input: previousSuggestedResponse.search
                            });
                          });
                        }
                      "
                      clickable
                      class="suggestion-chip --text-sm --shadow__bottom --bordered text-weight-medium"
                      style="height: auto"
                    >
                      <div class="column">
                        <div>
                          {{ previousSuggestedResponse.search }}
                        </div>
                        <div class="text-text-light" style="font-size: 11px">
                          {{ previousSuggestedResponse.helper }}
                        </div>
                      </div>
                    </q-chip>
                  </div>
                </div>
              </HorizontalScrollable>
              <q-separator class="q-my-sm" />
            </div>
          </transition>

          <div v-if="userInput.length == 0">
            <div class="q-px-md q-my-sm q-pb-xs">
              <div class="flex items-center justify-start q-mb-sm">
                <div class="text-weight-medium" style="font-size: 14px">
                  Search by Style
                </div>
              </div>
              <div class="flex">
                <q-chip
                  v-for="(vehicleStylesSearchOption,
                  i) in vehicleStylesSearchOptions"
                  :key="i + '_searchInputBodyType'"
                  color="background-light"
                  text-color="white"
                  @click="
                    e => {
                      handleSubmit(
                        'filterClause',
                        vehicleStylesSearchOption.filterClause
                      );
                    }
                  "
                  clickable
                  class="suggestion-chip --text-xs --shadow__bottom --bordered text-weight-medium"
                  style="width: 90px; height: 80px"
                  :style="[windowWidth <= 769 ? { 'width': '102px' } : {}]"
                >
                  <div
                    class="column items-center justify-end no-wrap q-py-sm"
                    style="width: 100%; height: 80px;"
                  >
                    <img
                      :src="
                        require(`@/assets/images/icons/vehicles/${vehicleStylesSearchOption.icon}`)
                      "
                      style="width: 64px; height: 64px;"
                    />
                    <div>
                      {{ vehicleStylesSearchOption.label }}
                    </div>
                  </div>
                </q-chip>
              </div>
            </div>

            <q-separator inset class="q-my-md" />

            <div class="q-px-md q-my-sm q-pb-xs">
              <div class="flex items-center justify-start q-mb-sm">
                <div class="text-weight-medium" style="font-size: 14px">
                  Search by Price
                </div>
              </div>
              <div class="flex">
                <q-chip
                  v-for="(priceOption, i) in vehiclePriceSearchOptions"
                  :key="i + '_searchInputPrice'"
                  color="background-light"
                  text-color="white"
                  @click="
                    e => {
                      handleSubmit('filterClause', priceOption.filterClause);
                    }
                  "
                  clickable
                  class="suggestion-chip --text-sm --shadow__bottom --bordered text-weight-medium"
                  style="padding: 16px 14px;"
                >
                  <div>{{ priceOption.label }}</div>
                </q-chip>
              </div>
            </div>

            <q-separator
              v-if="!hideRecommendedSearches"
              inset
              class="q-my-md"
            />

            <div
              v-if="!hideRecommendedSearches"
              class="q-px-md q-my-sm q-pb-xs"
            >
              <div class="flex items-center justify-start q-mb-sm">
                <div class="text-weight-medium" style="font-size: 14px">
                  Trending Searches
                </div>
              </div>
              <horizontal-scrollable
                v-if="recommendedSearches.length > 0"
                class="search-input-bar__suggestions-container"
              >
                <div class="search-input-bar__suggestions--stacked q-pr-md">
                  <div
                    v-for="(recommendedSearch, i) in recommendedSearches"
                    :key="i + '_recommendedSearch'"
                  >
                    <q-chip
                      color="background-light"
                      text-color="white"
                      @mousedown="horizontalScrollClickStart"
                      @mouseup="
                        e => {
                          horizontalScrollClickEnd(e, () => {
                            handleSubmit('input', {
                              input: recommendedSearch
                            });
                          });
                        }
                      "
                      clickable
                      class="suggestion-chip --text-sm --shadow__bottom --bordered text-weight-medium"
                      style="height: auto"
                    >
                      <div>{{ recommendedSearch }}</div>
                    </q-chip>
                  </div>
                </div>
              </horizontal-scrollable>
              <horizontal-scrollable
                v-else
                class="search-input-bar__suggestions-container"
              >
                <div class="search-input-bar__suggestions--stacked q-pr-md">
                  <q-card
                    v-for="i in 10"
                    :key="i + '_recommendedSearchPlaceholder'"
                    class="q-pa-none q-mr-sm q-mt-sm"
                    style="width: 160px; min-width: 160px; height: 28px"
                    flat
                  >
                    <q-skeleton
                      animation="fade"
                      class="full-width full-height"
                    />
                  </q-card>
                </div>
              </horizontal-scrollable>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- TODO: Quick win: Add basic "Watchlist table" -->
    <!-- TODO: populate input bar with anything static/new, etc -->
    <!-- TODO: Get "Ask CarScience" panel populating/working and combine standard + ai search ("AI Filters" filter tag style) -->
  </div>
</template>

<script>
import voiceRecognition from "@/mixins/voiceRecognition";
import HorizontalScrollable from "@/components/UI/HorizontalScrollable.vue";
import axios from "axios";

export default {
  name: "SearchInputBar",
  components: {
    HorizontalScrollable
  },
  mixins: [voiceRecognition],
  props: {
    disable: {
      type: Boolean,
      default: false
    },
    addShadows: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      userInput: "",
      userInputFocused: false,
      searchActive: false,
      recommendedSearches: [],
      hideRecommendedSearches: false,
      autocompleteSuggestions: [],
      autocompleteSuggestionsLoading: false,
      pendingAutocompleteSuggestionsRequests: [],
      activeDropdownAutocompleteSuggestionIndex: -1,
      vehicleStylesSearchOptions: [
        {
          label: "SUVs",
          icon: "suv.svg",
          filterClause: { body_type: { in: ["SUV"] } }
        },
        {
          label: "Sedans",
          icon: "sedan.svg",
          filterClause: { body_type: { in: ["Sedan"] } }
        },
        {
          label: "Trucks",
          icon: "truck.svg",
          filterClause: { body_type: { in: ["Truck"] } }
        },
        {
          label: "EVs",
          icon: "ev.svg",
          filterClause: { fuel_type: { in: ["Electric"] } }
        },
        {
          label: "Hybrids",
          icon: "hybrid.svg",
          filterClause: { fuel_type: { in: ["Hybrid"] } }
        },
        {
          label: "Coupes",
          icon: "coupe.svg",
          filterClause: { body_type: { in: ["Coupe"] } }
        },
        {
          label: "Hatchbacks",
          icon: "hatchback.svg",
          filterClause: { body_type: { in: ["Hatchback"] } }
        },
        {
          label: "Wagons",
          icon: "wagon.svg",
          filterClause: { body_type: { in: ["Wagon"] } }
        },
        {
          label: "Convertibles",
          icon: "convertible.svg",
          filterClause: { body_type: { in: ["Convertible"] } }
        },
        {
          label: "Minivans",
          icon: "minivan.svg",
          filterClause: { body_type: { in: ["Van"] } }
        }
      ],
      vehiclePriceSearchOptions: [
        {
          value: 10000,
          label: "Under $10k",
          filterClause: { price: { lte: 10000 } }
        },
        {
          value: 15000,
          label: "Under $15k",
          filterClause: { price: { lte: 15000 } }
        },
        {
          value: 20000,
          label: "Under $20k",
          filterClause: { price: { lte: 20000 } }
        },
        {
          value: 25000,
          label: "Under $25k",
          filterClause: { price: { lte: 25000 } }
        },
        {
          value: 30000,
          label: "Under $30k",
          filterClause: { price: { lte: 30000 } }
        },
        {
          value: 40000,
          label: "Under $40k",
          filterClause: { price: { lte: 40000 } }
        },
        {
          value: 50000,
          label: "Under $50k",
          filterClause: { price: { lte: 50000 } }
        },
        { value: "", label: "$50k+", filterClause: { price: { gte: 50000 } } }
      ],
      continueViewingComponentData: {
        title: "",
        component: "jfpc-vehicle-listings-visitor-recently-clicked",
        hide: !window.localStorage.getItem("jfpc-clicked-vehicles"),
        hideHeader: false,
        hideSeeMore: true,
        preventSeeMoreRedirect: true,
        key: "jfpc-vehicle-listings-visitor-recently-clicked",
        cardDesign: "tw-vehicle-listing-card-horizontal"
      },
      usingAutocompleteSuggestionsKeyboardNavigation: false,
      isSuggestedSearchesScrolledToBottom: false
    };
  },
  computed: {
    previousSuggestedResponses() {
      let previousChatMessage =
        window.localStorage.getItem("previous-chat-messages") !== null
          ? JSON.parse(
              window.localStorage.getItem("previous-chat-messages")
            ).findLast(message => message.fromUser)
          : null;
      if (
        previousChatMessage &&
        window.localStorage.getItem("previous-suggested-responses")
      ) {
        let previousSuggestedResponses = JSON.parse(
          window.localStorage.getItem("previous-suggested-responses")
        );
        return [
          {
            search: previousChatMessage.message,
            helper:
              "Searched " +
              (window.localStorage.getItem(
                "time-previous-chat-message-sent"
              ) !== null
                ? this.$dayjs(
                    window.localStorage.getItem(
                      "time-previous-chat-message-sent"
                    )
                  ).fromNow()
                : "previously")
          },
          {
            search: previousSuggestedResponses[0],
            helper: "Suggestion from your last search"
          },
          {
            search: previousSuggestedResponses[1],
            helper: "Suggestion from your last search"
          },
          {
            search: previousSuggestedResponses[2],
            helper: "Suggestion from your last search"
          }
        ];
      } else {
        return [];
      }
    }
  },
  watch: {
    $route() {
      this.userInput = "";
    },
    userInput(newVal) {
      if (newVal.length === 0) {
        this.autocompleteSuggestions = [];
      } else {
        this.fetchAutocompleteSuggestions();
      }
    },
    userInputFocused(newVal) {
      if (!newVal && this.searchActive) {
        // Input lost focus but search is active (mobile keyboard dismissed)
        // Keep search active
        return;
      }
    },
    searchActive(newVal) {
      if (newVal && this.recommendedSearches.length === 0) {
        this.initRecommendedSearches();
      }
    }
  },
  mounted() {
    this.initVoiceRecognition(this.handleVoiceRecognitionInput);
    this.initClickOutsideListener();
    this.initWindowBlurListener();
  },
  methods: {
    handleVoiceRecognitionInput(input) {
      this.userInput = input;
      this.handleSubmit("input", { input: this.userInput });
    },
    fetchAutocompleteSuggestions() {
      this.autocompleteSuggestionsLoading = true;
      this.moveDropdownAutocompleteSuggestionFocus(0, 0);
      this.usingAutocompleteSuggestionsKeyboardNavigation = false;

      if (this.pendingAutocompleteSuggestionsRequests.length > 0) {
        this.pendingAutocompleteSuggestionsRequests.forEach(controller => {
          controller.abort();
        });
        this.pendingAutocompleteSuggestionsRequests = [];
      }
      let controller = new AbortController();
      let { signal } = controller;
      this.pendingAutocompleteSuggestionsRequests.push(controller);

      axios({
        url:
          "https://api-v2.jumpfeed.com/network/publisher/products/vehicles/autocomplete",
        method: "POST",
        signal: signal,
        data: {
          keyword: this.userInput,
          limit: 15
        }
      }).then(response => {
        let suggestions = response.data.data.results;
        suggestions.forEach((suggestion, i) => {
          suggestions[i].active = false;
          suggestions[i].aiSuggestion = !(
            typeof suggestion.search_filters === "object" &&
            !Array.isArray(suggestion.search_filters) &&
            suggestion.search_filters !== null
          );
        });
        this.autocompleteSuggestions = suggestions;
        this.autocompleteSuggestionsLoading = false;
      });
    },
    highlightedAutocompleteSuggestion(suggestion) {
      const searchTerm = this.userInput.trim();

      if (!searchTerm) {
        return suggestion;
      }

      let searchTerms = searchTerm.split(" ");
      let highlightRegex = "";
      searchTerms.forEach(term => {
        let cleanedTerm = term.replace(
          /(<span class="text-text text-weight-medium">|<\/span>)/gim,
          ""
        );
        highlightRegex += cleanedTerm + "|";
      });
      let regex = new RegExp(highlightRegex, "gi");
      let highlightedSuggestion = suggestion.replace(
        regex,
        '<span class="text-text text-weight-medium">$&</span>'
      );

      return highlightedSuggestion;
    },
    initRecommendedSearches() {
      axios
        .post(
          "https://api-v2.jumpfeed.com/network/publisher/carscience-search-suggestions-open-router-v2",
          {
            visitor_profile: {
              clickCounts: this.generateVisitorProfile(),
              filterCounts: this.$store.getters[
                "search/searchFiltersHistoryByClauseCount"
              ],
              filterHistory: this.$store.getters[
                "search/searchFiltersHistory"
              ].slice(0, 20)
            },
            num_suggestions: 10,
            has_previous_history:
              this.$store.state.chat.conversationHistory.length > 0 ? 1 : 0
          }
        )
        .then(response => {
          let output = response.data.data;
          output.reply.forEach(suggestion => {
            this.recommendedSearches.push(suggestion);
          });
        })
        .catch(e => {
          this.hideRecommendedSearches = true;
          console.error(e);
        });
    },
    initClickOutsideListener() {
      let searchWidgetDiv = this.$el;

      // Add a click event listener to the entire document
      document.addEventListener("click", event => {
        if (
          !searchWidgetDiv.contains(event.target) ||
          event.target.closest(".q-btn") !== null
        ) {
          this.closeSearch();
        } else if (event.target.closest(".suggestion-chip") === null) {
          // Detect if the clicked element is performing handleSubmit, and if so, dont do the 2 lines below
          this.searchActive = true;
          this.$refs["SearchInput"].focus();
        }
      });
    },
    moveDropdownAutocompleteSuggestionFocus(direction, resetIndex = null) {
      let newIndex = this.activeDropdownAutocompleteSuggestionIndex + direction;
      if (resetIndex !== null) {
        newIndex = resetIndex;
      }

      if (newIndex >= 0 && newIndex < this.autocompleteSuggestions.length) {
        this.activeDropdownAutocompleteSuggestionIndex = newIndex;

        this.$nextTick(() => {
          const target = this.$refs[
            newIndex + "_dropdownAutocompleteSuggestionItem"
          ][0];
          if (target && target.scrollIntoView) {
            target.scrollIntoView({ block: "center", behavior: "smooth" });
          }
        });
      }
    },
    triggerAutocompleteSuggestionsKeyboardNav() {
      this.usingAutocompleteSuggestionsKeyboardNavigation = true;
      let container = document.getElementsByClassName(
        "suggested-searches-container"
      )[0];
      if (container) {
        container.focus();
      }
    },
    handleSuggestedSearchesScroll(event) {
      const container = event.target;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Check if scrolled to bottom (with 1px tolerance)
      this.isSuggestedSearchesScrolledToBottom =
        scrollTop + clientHeight >= scrollHeight - 1;
    },
    initWindowBlurListener() {
      window.addEventListener("blur", () => {
        this.closeSearch();
      });
    },
    handleInputFocus() {
      this.userInputFocused = true;
      this.searchActive = true;
    },
    handleInputBlur() {
      this.userInputFocused = false;
      // Don't automatically close search on blur - let click handlers manage it
      // This allows mobile keyboard to hide while keeping dropdown open
    },
    closeSearch() {
      this.searchActive = false;
      this.userInputFocused = false;
      this.userInput = '';
      this.$refs['SearchInput'].blur();
    },
    handleSubmit(searchType, searchObject) {
      if (searchType === "filterClause") {
        this.$store.commit("search/SET_SEARCH_FILTERS", [searchObject]);
      } else if (searchType === "filterClauses") {
        // Convert search_filters object to array of filter clauses
        const filterClauses = this.convertSearchFiltersToFilterClauses(
          searchObject
        );
        this.$store.commit("search/SET_SEARCH_FILTERS", filterClauses);
      } else {
        this.$store.commit("chat/ADD_MESSAGE", {
          fromUser: true,
          message: searchObject.input
        });
        this.$store.commit("chat/SET_SIDEBAR", true);
      }

      this.$store.commit("search/RESET_PAGINATION");
      this.$store.commit("search/SET_LOADING", true);

      if (this.$route.name !== "search") {
        this.$router.push({ name: "search" });
      }

      setTimeout(() => {
        this.closeSearch();
      }, 1);
    },
    convertSearchFiltersToFilterClauses(searchFilters) {
      // Body type ID to name mapping
      const bodyTypeMapping = {
        "1": "Sedan",
        "2": "Truck",
        "3": "SUV",
        "4": "Van",
        "5": "Coupe",
        "6": "Hatchback",
        "7": "Wagon",
        "8": "Convertible"
      };

      const filterClauses = [];

      // Convert each search_filter to appropriate filter clause
      for (const [field, condition] of Object.entries(searchFilters)) {
        if (field === "body_type_id") {
          // Remap body_type_id to body_type with name
          const bodyTypeId = condition.equals;
          const bodyTypeName = bodyTypeMapping[bodyTypeId];
          if (bodyTypeName) {
            filterClauses.push({
              body_type: { in: [bodyTypeName] }
            });
          }
        } else if (condition.equals !== undefined) {
          // Convert all other fields with "equals" to "in" array format
          filterClauses.push({
            [field]: { in: [condition.equals] }
          });
        }
      }

      return filterClauses;
    }
  }
};
</script>

<style lang="scss" scoped>
.search-input-bar {
  width: 100%;
  position: relative;
  z-index: 3000;
  max-width: 820px;

  @include screen-md {
    z-index: 99;

    &.--open {
      z-index: 9999;
      position: fixed;
      top: 12px;
      left: 12px;
      right: 12px;
      bottom: 0;
      width: calc(100% - 24px) !important;
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }

  .search-input-bar__form {
    .search-input-bar__input {
      position: relative;
      z-index: 3003;
      margin-top: -4px;
      font-size: 16px !important;
      margin-left: auto;
      margin-right: auto;
    }
  }

  .search-widget__backdrop {
    position: fixed;
    top: 66px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(2px);
    z-index: 3001;
    pointer-events: none;
  }

  .search-widget__dropdown-container {
    position: absolute;
    z-index: 3002;
    top: 60px;
    left: 0;
    right: 0;
    background: color(background, light);
    box-shadow: 0 6px 12px rgba(1, 1, 1, 0.6), 0 6px 12px rgba(1, 1, 1, 0.9),
      0 3px 3px -4px rgba(1, 1, 1, 0.62);
    padding: 8px 0;
    border-radius: 16px;
    border: 1px solid color(border);
    overflow: auto;
    max-height: calc(100dvh - 62px);
    width: 100% !important;

    @include screen-sm {
      padding-bottom: 482px;
    }

    .suggested-searches-container {
      margin-bottom: -8px;
      margin-top: 8px;

      mask-image: linear-gradient(
        to bottom,
        transparent 0%,
        black 0%,
        black 82%,
        transparent 100%
      );
      padding-bottom: 8px;

      &::-webkit-scrollbar {
        display: none;
      }

      &.--scrolled-to-bottom {
        mask-image: none;
      }
    }
  }

  .search-input-bar__suggestions--stacked {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    min-height: 84px;
    width: 400%;
    min-width: 200%;

    @include screen-md() {
      width: 400%;
      min-width: 345%;
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .suggestion-chip {
    @include hover-scale(1.03);
    @include transition();
    padding-top: 6px;
    padding-bottom: 6px;
    background: color(background, very-light);

    @media (min-width: #{$screen-sm}) {
      &:hover {
        border: 1px solid color(secondary);
      }
    }
  }

  .suggestion-chip--min-hover {
    @include hover-scale(1.015);
  }

  .--autocomplete-suggestion-hover {
    transform: scale(1.015);
    background: #505052;
  }
}

.search-input-bar__suggestions-container {
  width: 100%;
  overflow-x: auto;
  padding: 0;
  margin-top: -10px;

  &::-webkit-scrollbar {
    display: none;
  }
}

.outline-none:focus {
  outline: none;
}
</style>
<style lang="scss">
.search-input-bar__input {
  .q-field__control,
  .q-field__marginal {
    height: 50px !important;
  }
}
</style>
